import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Resizable, ResizeCallbackData } from 'react-resizable';
import 'react-resizable/css/styles.css';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { Endpoint } from '@jsplumb/community/types/core';
import HolderOutlined from '@ant-design/icons/HolderOutlined';
import CaretRightFilled from '@ant-design/icons/CaretRightFilled';
import CaretDownFilled from '@ant-design/icons/CaretDownFilled';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import { eventHistory } from '@kemu-io/kemu-core/common';
import { repeatLastReceivedEvent } from '@kemu-io/kemu-core/processor';
import { EventHistoryItem } from '@kemu-io/kemu-core/types';
import { DataType, SupportedTypes } from '@kemu-io/hs-types';
import classNames from 'classnames';
import { Inspector, ObjectRootLabel, ObjectLabel } from 'react-inspector';
import { useSelector, useDispatch } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import { Button, message, Tooltip } from 'antd';
import Icon, { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { decodeType } from '@kemu-io/kemu-core/common/utils';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';
import styles from './WidgetPortInspector.module.css';
import { customTheme } from './inspectorTheme.ts';
import ScriptCreationAssistant from './ScriptCreationAssistant.tsx';
import { VirtualizedArrayRenderer } from './VirtualizedArrayRenderer';
import { recursivelyReplaceBase64,
  LARGE_ARRAY_THRESHOLD,
  INITIAL_ARRAY_ITEMS,
  parseLoadMoreMarker,
  renderArrayCount,
  renderLoadMoreButton,
  getArrayMetadataFromPaginatedArray,
  processNestedArrays as processNestedArraysHelper } from './helpers';
import { isBase64String } from '@kemu-io/kemu-core/common/lib/utils/typeCheckUtils.js';
import { ReactComponent as AiAssistedIcon } from '@src/assets/img/ai-assisted-icon.svg';
import { ReactComponent as ReplayIcon } from '@src/assets/img/dataTypes/replay-icon.svg';
import { LM_CANVAS_CONTAINER_CLASS, GATE_PORT_CLASS, TRIGGER_PORT_CLASS, TARGET_PORT_CLASS } from '@common/constants';
import { dataTypeToHumanReadable, decodeDomId } from '@common/utils';
import { KemuEndpointData } from '@src/types/core_t';
import { useTranslation } from '@hooks/index';
import { selectCurrentRecipePoolId, selectOpeningRecipe } from '@src/features/Workspace/workspaceSlice.ts';
import { selectLogicMapperSettings, setLogicMapperSettingValue } from '@src/features/interface/interfaceSlice.ts';
import { useMinimizedPanels } from '@components/MinimizedPanelsContainer/MinimizedPanelsContext';
import usePanelMinimize from '@src/hooks/usePanelMinimize';
import PanelMinimizeButton from '@components/PanelMinimizeButton/PanelMinimizeButton';

const minContractedPanelSize = { width: 300, height: 200 };
const minExpandedPanelSize = { width: 300, height: 200 };
const ignoreObjectTypes = [DataType.AudioBuffer, DataType.ArrayBuffer, DataType.ImageBitmap];

type ElementWithJTKEndpoint = HTMLElement & {
  jtk?: {
    endpoint: Endpoint;
  }
}

type PortInfo = {
  isOutput: boolean;
  portType: DataType | DataType[];
  lastTimestamp?: number;
  lastDetectedEventType?: DataType;
  widgetId: string;
  portName: string;
  lastDetectedValue?: string | number | boolean | Record<string, unknown>;
  lastValueIsObject?: boolean;
  isTriggerPort?: boolean;
}

type Props = {
  containerEl: HTMLDivElement | null;
}

const PORT_INSPECTOR_WIDTH = minContractedPanelSize.width;
const PORT_INSPECTOR_HEIGHT = minContractedPanelSize.height;
const PORT_INSPECTOR_MARGIN = 10;

/**
 * Loops through all the properties of an object and removes any that are not serializable
 * such as ArrayBuffer, ImageBitmap, etc.
 * @param obj 
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const removeArrayBufferProps = (obj: any): SupportedTypes => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => removeArrayBufferProps(item)) as SupportedTypes;
  }

  if (obj instanceof ImageData) {
    return {
      width: obj.width,
      height: obj.height,
      colorSpace: obj.colorSpace,
      data: '<Uint8ClampedArray>',
    };
  }

  const finalObj: Record<string, unknown> = {};
  const objKeys = Object.keys(obj);

  objKeys.forEach((key) => {
    const value = obj[key];
    if (value instanceof ArrayBuffer) {
      finalObj[key] = '<ArrayBuffer>';
    } else if (value instanceof ImageData) {
      finalObj[key] = '<ImageData>';
    } else if (value instanceof AudioBuffer) {
      finalObj[key] = '<AudioBuffer>';
    } else if (value instanceof Uint8ClampedArray) {
      finalObj[key] = '<Uint8ClampedArray>';
    } else if (Array.isArray(value)) {
      finalObj[key] = value.map((item) => removeArrayBufferProps(item));
    } else if (typeof value === 'object') {
      finalObj[key] = removeArrayBufferProps(value as Record<string, unknown>);
    } else {
      finalObj[key] = value;
    }
  });

  return finalObj as SupportedTypes;
};

const WidgetPortInspector = (props: Props) => {
  const { containerEl } = props;
  const userSettings = useSelector(selectLogicMapperSettings);
  const defaultPanelPosition = userSettings.portInspectorPosition;
  const defaultPanelSize = userSettings.portInspectorSize || minContractedPanelSize;
  const [panelSize, setPanelSize] = useState<{ width: number, height: number }>(defaultPanelSize);
  const [portInfo, setPortInfo] = useState<PortInfo | null>(null);
  const [lastExpandedSize, setLastExpandedSize] = useState<{ width: number, height: number }>({ ...minExpandedPanelSize });
  const [expandObjectDetails, setExpandObjectDetails] = useState(false);
  const [showRawBase64, setShowRawBase64] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);
  const [scriptAssistantVisible, setScriptAssistantVisible] = useState(false);
  const [panelPosition, setPanelPosition] = useState(defaultPanelPosition);
  const { ref, width, height } = useResizeDetector();
  const recipePoolId = useSelector(selectCurrentRecipePoolId);
  const openingRecipe = useSelector(selectOpeningRecipe);
  const dispatch = useDispatch();
  const t = useTranslation();
  const wpT = useMemo(() => t.withBaseKey('LogicMapper.WidgetPortDebugPanel'), [t]);
  const { registerExpandHandler, unregisterPanel } = useMinimizedPanels();

  const { isMinimized, handleMinimize, handleExpand } = usePanelMinimize({
    panelId: 'portInspector',
    currentPosition: panelPosition,
    currentSize: panelSize,
    setPanelPosition,
    setPanelSize,
  });
  const portTypes = Array.isArray(portInfo?.portType) ? portInfo?.portType : [portInfo?.portType];
  const portTypeNames = portTypes?.map((type) => dataTypeToHumanReadable(t, type as DataType));
  const showDetailsPanel = portInfo?.lastValueIsObject && expandObjectDetails;

  // Track visible counts for nested arrays using path-based keys
  const [nestedArrayVisibleCounts, setNestedArrayVisibleCounts] = useState<Map<string, number>>(new Map());

  // Reset nested array counts when port info changes
  useEffect(() => {
    setNestedArrayVisibleCounts(new Map());
  }, [portInfo?.lastDetectedValue]);

  // Wrapper for processNestedArrays that provides the visible counts map
  const processNestedArrays = useCallback((data: unknown, path: string = ''): unknown => {
    return processNestedArraysHelper(data, nestedArrayVisibleCounts, path);
  }, [nestedArrayVisibleCounts]);

  // Helper to handle load more for nested arrays
  const handleNestedArrayLoadMore = useCallback((arrayPath: string, arrayLength: number) => {
    setNestedArrayVisibleCounts(prev => {
      const newMap = new Map(prev);
      const currentCount = prev.get(arrayPath) ?? INITIAL_ARRAY_ITEMS;
      newMap.set(arrayPath, Math.min(currentCount + INITIAL_ARRAY_ITEMS, arrayLength));
      return newMap;
    });
  }, []);

  const handleCopy = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!portInfo?.lastDetectedValue) {
      return;
    }

    const valueToCopy = typeof portInfo.lastDetectedValue === 'object'
      ? JSON.stringify(portInfo.lastDetectedValue, null, 2)
      : portInfo.lastDetectedValue.toString();

    navigator.clipboard.writeText(valueToCopy).then(() => {
      setCopied(true);
      message.success(wpT('ValueCopied'));
      setTimeout(() => {
        setCopied(false);
      }, 1000);
    });
  }, [portInfo, wpT]);

  const handleOpenScriptAssistant = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setScriptAssistantVisible(true);
  }, []);

  const handleCloseScriptAssistant = useCallback(() => {
    setScriptAssistantVisible(false);
  }, []);

  const handleRepeatLastEvent = useCallback(async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (!portInfo?.lastDetectedValue) {
      return;
    }

    await repeatLastReceivedEvent({
      targetPortName: portInfo.portName,
      thingId: DEFAULT_THING_ID,
      widgetId: portInfo.widgetId,
    });
  }, [portInfo]);

  const handleResize = (event: React.SyntheticEvent, data: ResizeCallbackData) => {
    let newWidth = Math.max(minContractedPanelSize.width, data.size.width);
    let newHeight = Math.max(minContractedPanelSize.height, data.size.height);
    const rightEdge = panelPosition.x + newWidth + PORT_INSPECTOR_MARGIN;
    const bottomEdge = panelPosition.y + newHeight + PORT_INSPECTOR_MARGIN;

    if (width && rightEdge > width) {
      newWidth = width - panelPosition.x - PORT_INSPECTOR_MARGIN;
    }

    if (height && bottomEdge > height) {
      newHeight = height - panelPosition.y - PORT_INSPECTOR_MARGIN;
    }

    if (expandObjectDetails) {
      setLastExpandedSize({
        width: newWidth,
        height: newHeight,
      });
    }

    const finalSize = {
      width: newWidth,
      height: newHeight,
    };

    setPanelSize(finalSize);

    dispatch(setLogicMapperSettingValue({
      key: 'portInspectorSize',
      value: finalSize,
    }));
  };

  const togglePanelExpansion = () => {
    const nextExpanded = !expandObjectDetails;
    if (nextExpanded) {
      // Make sure the expanded dimensions are large enough
      const nextW = panelSize.height < minExpandedPanelSize.height ? minExpandedPanelSize.height : panelSize.height;
      const nextH = panelSize.width < minExpandedPanelSize.width ? minExpandedPanelSize.width : panelSize.width;
      setPanelSize({ width: Math.max(lastExpandedSize.width, nextW), height: Math.max(lastExpandedSize.height, nextH) });
    } else {
      setPanelSize({ width: minContractedPanelSize.width, height: minContractedPanelSize.height });
    }

    setExpandObjectDetails(!expandObjectDetails);
  };

  const handleStopDragging = (_: DraggableEvent, data: DraggableData) => {
    setPanelPosition({
      x: data.x,
      y: data.y,
    });

    dispatch(setLogicMapperSettingValue({
      key: 'portInspectorPosition',
      value: {
        x: data.x,
        y: data.y,
      },
    }));
  };


  const handleToggleRawBase64 = useCallback((e: React.MouseEvent<HTMLButtonElement>, path: string) => {
    e.stopPropagation();
    setShowRawBase64(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  }, []);

  const Base64Renderer = useCallback(({ base64Data, identifier, showName = true }: { base64Data: string, identifier: string, showName?: boolean }) => {
    const isShowingRaw = showRawBase64[identifier] || false;

    return (
      <span className={styles.Base64Container}>
        {showName && (
          <span><span className={styles.Base64Name}>{identifier}</span>: </span>
        )}
        {isShowingRaw ? (
          <span className={styles.Base64RawValue}>
            {`"${base64Data.length > 500 ? base64Data.substring(0, 500) + '...' : base64Data}"`}
          </span>
        ) : (
          <span className={styles.Base64Placeholder}>&lt;Base64 Encoded String&gt;</span>
        )}
        <Tooltip title={ isShowingRaw ? wpT('HideRawValue') : wpT('SeeRawValue') } placement="top">
          <Button
            type="text"
            icon={isShowingRaw ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleToggleRawBase64(e, identifier)}
            className={styles.Base64ToggleButton}
            size="small"
          />
        </Tooltip>
      </span>
    );
  }, [showRawBase64, handleToggleRawBase64, wpT]);

  /**
   * Custom node renderer for the Inspector component that handles different data types
   * with special rendering logic for Base64 strings, object structures, and nested arrays.
   *
   * @param depth - The depth level of the node in the object tree
   * @param name - The property name/key of the current node
   * @param data - The actual data value to be rendered
   * @param isNonenumerable - Whether this property is non-enumerable
   * @returns React element representing the rendered node
   */
  const customNodeRenderer = useCallback(({ depth, name, data, isNonenumerable }: { depth: number, name: string, data: unknown, isNonenumerable: boolean }) => {
    const isBase64 = isBase64String(data as string);
    if (isBase64 && name) {
      const base64Data = data as string;
      return (<Base64Renderer base64Data={base64Data} identifier={name} showName={true} />);
    }

    // Handle nested array load more marker
    if (typeof data === 'string') {
      const parsed = parseLoadMoreMarker(data);
      if (parsed && parsed.path !== '') {
        const currentCount = nestedArrayVisibleCounts.get(parsed.path) ?? INITIAL_ARRAY_ITEMS;
        const arrayLength = parsed.remaining + currentCount;
        return renderLoadMoreButton(parsed.remaining, () => handleNestedArrayLoadMore(parsed.path, arrayLength), wpT);
      }
    }

    // Handle nested large arrays (depth > 0) - show count with visible/total format
    if (!isBase64 && depth > 0 && Array.isArray(data)) {
      const metadata = getArrayMetadataFromPaginatedArray(data, nestedArrayVisibleCounts);
      return renderArrayCount(metadata?.visibleCount ?? data.length, metadata?.totalCount, name);
    }

    // Handle array root label - for small arrays, show count
    if (!isBase64 && depth === 0) {
      if (Array.isArray(data)) {
        return renderArrayCount(data.length);
      }
      return <ObjectRootLabel name={name} data={recursivelyReplaceBase64(data)} />;
    }
    return <ObjectLabel name={name} data={recursivelyReplaceBase64(data)} isNonenumerable={isNonenumerable} />;
  }, [Base64Renderer, nestedArrayVisibleCounts, handleNestedArrayLoadMore, wpT]);

  const processPortClick = useCallback((e: HTMLElement) => {
    const target = e as ElementWithJTKEndpoint;
    const isTriggerPort = target.classList.contains(TRIGGER_PORT_CLASS);
    const isTargetPort = target.classList.contains(TARGET_PORT_CLASS);
    const endpointPort = target.jtk?.endpoint;
    const endpointElement = endpointPort?.element as HTMLElement;
    const widgetInfo = decodeDomId(endpointElement.id);
    const portData = endpointPort?.getData() as KemuEndpointData;
    let history: EventHistoryItem | null = null;

    if (widgetInfo?.gateId) {
      if (isTargetPort) {
        history = eventHistory.getLastReceivedPortEvent(widgetInfo?.blockId, widgetInfo?.gateId, portData.name);
      } else {
        history = eventHistory.getLastProducedPortEvent(widgetInfo?.blockId, widgetInfo?.gateId, portData.name);
      }
    }

    let lastValue = history?.data.value;
    const lastValueIsObject = typeof history?.data.value === 'object'
            && !ignoreObjectTypes.includes(history?.data.type as DataType);

    if (lastValueIsObject) {
      lastValue = removeArrayBufferProps(lastValue);
    }

    setPortInfo({
      isOutput: !isTargetPort,
      portType: portData.type,
      lastDetectedEventType: history?.data.type as DataType,
      lastDetectedValue: lastValue as string | number | boolean | Record<string, unknown>,
      lastTimestamp: history?.data.timestamp as number,
      lastValueIsObject,
      widgetId: widgetInfo?.gateId || '',
      portName: portData.name,
      isTriggerPort,
    });
  }, []);

  useEffect(() => {
    if (!recipePoolId) { return; }
    const canvasContainer = document.querySelector(`.${LM_CANVAS_CONTAINER_CLASS}`);

    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains(GATE_PORT_CLASS)) {
        processPortClick(target);
      }
    };

    canvasContainer?.addEventListener('click', handleClick as EventListener);

    return () => {
      canvasContainer?.removeEventListener('click', handleClick as EventListener);
    };
  }, [processPortClick, recipePoolId]);

  useEffect(() => {
    if (containerEl) {
      ref.current = containerEl;
    }
  }, [containerEl, ref]);

  // Register/unregister expand handler with context
  useEffect(() => {
    if (isMinimized) {
      registerExpandHandler('portInspector', handleExpand, wpT('Title'));
    } else {
      unregisterPanel('portInspector');
    }
    return () => {
      unregisterPanel('portInspector');
    };
  }, [isMinimized, handleExpand, wpT, registerExpandHandler, unregisterPanel]);

  // Prevents panel from being outside the visible workspace
  useEffect(() => {
    if (width && height) {
      setPanelPosition((current) => {
        const panelX = width && width < (current.x + PORT_INSPECTOR_WIDTH + PORT_INSPECTOR_MARGIN) ? width - PORT_INSPECTOR_WIDTH - PORT_INSPECTOR_MARGIN : current.x;
        const panelY = height && height < (current.y + PORT_INSPECTOR_HEIGHT + PORT_INSPECTOR_MARGIN) ? height - PORT_INSPECTOR_HEIGHT - PORT_INSPECTOR_MARGIN : current.y;
        return {
          x: panelX,
          y: panelY,
        };
      });
    }
  }, [width, height]);

  const parsedValue = useMemo(() => {
    if (portInfo?.lastDetectedEventType === undefined ) {
      return null;
    }

    return portInfo.lastDetectedValue;
  }, [portInfo?.lastDetectedValue, portInfo?.lastDetectedEventType]);

  // Check if the parsed value is a large array that needs virtualization
  const isLargeArray = useMemo(() => {
    return Array.isArray(parsedValue) && parsedValue.length > LARGE_ARRAY_THRESHOLD;
  }, [parsedValue]);

  if (openingRecipe || !recipePoolId)  { return null; }

  // If minimized, don't render the full panel (it will be rendered by MinimizedPanelsContainer)
  if (isMinimized) {
    return null;
  }

  const lastValueIsPrintable = portInfo?.lastDetectedEventType === DataType.String
    || portInfo?.lastDetectedEventType === DataType.Number
    || portInfo?.lastDetectedEventType === DataType.Boolean;

  const textModeValue = parsedValue?.toString();
  const longTextMode = lastValueIsPrintable && (textModeValue?.length || 0) > 12;
  const lastValueIsJson = portInfo?.lastDetectedEventType === DataType.JsonObj || portInfo?.lastDetectedEventType === DataType.Rect;
  const lastValueIsString = portInfo?.lastDetectedEventType === DataType.String;
  const allowedTypes = [DataType.String, DataType.JsonObj, DataType.Array, DataType.Rect, DataType.Point, DataType.ImageData];
  const lastValueIsSerializableArray = Array.isArray(portInfo?.lastDetectedValue)
    && portInfo?.lastDetectedValue.every((item) => allowedTypes.includes(decodeType(item)));

  return (
    <>
      <Draggable
        onStop={handleStopDragging}
        handle={`.${styles.DragHandle}`}
        bounds="parent"
        position={panelPosition}
        disabled={isMinimized}
      >
        <Resizable
          height={panelSize.height}
          width={panelSize.width}
          minConstraints={showDetailsPanel
            ? [minExpandedPanelSize.width, minExpandedPanelSize.height]
            : [minContractedPanelSize.width, minContractedPanelSize.height]
          }
          resizeHandles={['se']}
          onResize={handleResize}
        >
          <div
            className={styles.Inspector}
            style={{
              width: panelSize.width,
              height: panelSize.height,
            }}
          >
            <div className={styles.DragHandle}>
              <span className={styles.DragIcon}>
                <HolderOutlined />
              </span>
              <span className={styles.DragText}>
                {wpT('Title')}
              </span>
              <PanelMinimizeButton onClick={handleMinimize} />
            </div>

            <div className={styles.InspectorBody}>
              {!portInfo && (
                <div className={styles.NoData}>
                  {wpT('ClickToInspect')}
                </div>
              )}


              {portInfo && (
                <>
                  <div className={styles.LineItem}>
                    <div className={styles.LineItemLabel}>{wpT('DataType')}:</div>
                    <div className={styles.LineItemValue}>
                    {portTypeNames?.length ? portTypeNames?.join(', ') : wpT('NotAvailable', 'N/A')}
                    </div>
                  </div>

                  <div className={styles.LineItem}>
                    <div className={styles.LineItemLabel}>{wpT('PortType')}:</div>
                    <div className={styles.LineItemValue}>
                      {portInfo?.isOutput ? wpT('SourcePort') : wpT('TargetPort')}
                    </div>
                  </div>

                  <div className={classNames(styles.LineItem)}>
                    <div className={classNames(styles.LineItemLabel, styles.Centered)}>{wpT('LastValue', 'Last Value')}:</div>
                    <div className={
                      classNames(styles.LineItemValue, styles.Result, {
                        [styles.Clickable]: portInfo?.lastValueIsObject,
                      })}
                      onClick={portInfo?.lastValueIsObject ? togglePanelExpansion : undefined}
                    >
                      {(portInfo?.lastDetectedValue !== undefined && portInfo?.lastDetectedValue !== null) && (
                        <div className={styles.ActionButtons}>
                          {(lastValueIsJson || lastValueIsString || lastValueIsSerializableArray) && (
                            <Tooltip
                              title={wpT('CopyToClipboard')}
                              placement="top"
                            >
                              <Button type="text" icon={copied ? <CheckOutlined /> : <CopyOutlined />} onClick={handleCopy} />
                            </Tooltip>
                          )}

                          <Tooltip
                            title={wpT('GenerateBtn.Tooltip')}
                            placement="top"
                          >
                            <Button type="text" icon={<Icon component={AiAssistedIcon} />} onClick={handleOpenScriptAssistant} />
                          </Tooltip>

                          {!portInfo.isOutput && (
                            <Tooltip
                              title={wpT('RepeatLastEvent')}
                              placement="top"
                            >
                              <Button type="text" icon={<Icon component={ReplayIcon} />} onClick={handleRepeatLastEvent} />
                            </Tooltip>
                          )}
                        </div>
                      )}
                      {portInfo?.lastValueIsObject ? (
                        <div className={styles.ExpandableValue}>
                          <span>
                            {Array.isArray(portInfo.lastDetectedValue)
                              ? dataTypeToHumanReadable(t, DataType.Array)
                              : ( portInfo?.lastDetectedEventType === DataType.ImageData
                                ? wpT('Image', 'Image')
                                : dataTypeToHumanReadable(t, DataType.JsonObj)
                              )
                            }
                          </span>
                          <span>
                            {expandObjectDetails ? (
                              <CaretDownFilled  />
                            ) : (
                              <CaretRightFilled />
                            )}
                          </span>
                        </div>
                      ): (
                        (portInfo?.lastDetectedValue !== undefined && portInfo?.lastDetectedValue !== null) ? (
                          <>
                            {portInfo?.lastDetectedEventType === DataType.ImageData && (
                              <span>{wpT('Image', 'Image')}</span>
                            )}

                            {portInfo?.lastDetectedEventType === DataType.AudioBuffer && (
                              <span>{wpT('Audio', 'Audio')}</span>
                            )}

                            {lastValueIsPrintable && (
                              longTextMode ? (
                                <span>
                                  <CaretDownFilled />
                                </span>
                              ) : (
                                <span>{textModeValue}</span>
                              )
                            )}
                          </>
                        ) : ('N/A')
                      )}
                    </div>
                  </div>

                  {lastValueIsPrintable && longTextMode && (
                    <div className={styles.TextModeValue}>
                      <span>{isBase64String(textModeValue as string) ? <Base64Renderer base64Data={textModeValue as string} identifier="" showName={false} /> : textModeValue}</span>
                    </div>
                  )}

                  <div className={classNames(styles.DetailsPanel,
                    {
                      [styles.Visible]: expandObjectDetails,
                    }
                  )}>
                    {
                      (
                        portInfo?.lastDetectedEventType === DataType.JsonObj
                        || portInfo?.lastDetectedEventType === DataType.Array
                        || portInfo?.lastDetectedEventType === DataType.Rect
                        || portInfo?.lastDetectedEventType === DataType.Point
                        || portInfo?.lastDetectedEventType === DataType.BinaryFile
                        || portInfo?.lastDetectedEventType === DataType.ImageData
                      ) && (
                        isLargeArray && Array.isArray(parsedValue) ? (
                          <VirtualizedArrayRenderer
                            key={`${portInfo?.widgetId}-${portInfo?.portName}`}
                            arrayData={parsedValue}
                            customNodeRenderer={customNodeRenderer}
                            processNestedArrays={processNestedArrays}
                            nestedArrayVisibleCounts={nestedArrayVisibleCounts}
                            handleNestedArrayLoadMore={handleNestedArrayLoadMore}
                          />
                        ) : (
                          <Inspector
                            data={processNestedArrays(parsedValue)}
                            table={false}
                            // First level always expanded
                            expandLevel={1}
                            // @ts-expect-error bad typings
                            theme={customTheme}
                            nodeRenderer={customNodeRenderer}
                          />
                        )
                      )
                    }
                  </div>
                </>
              )}
            </div>
          </div>
        </Resizable>
      </Draggable>

      <ScriptCreationAssistant
        visible={scriptAssistantVisible}
        onCancel={handleCloseScriptAssistant}
        portValue={portInfo?.lastDetectedValue}
        portValueType={portInfo?.lastDetectedEventType === DataType.JsonObj ? 'json' : 'text'}
        widgetId={portInfo?.widgetId || ''}
        recipeId={recipePoolId || ''}
      />
    </>
  );
};

export default WidgetPortInspector;
